/* eslint-disable @typescript-eslint/no-unused-vars */
import { Download, Visibility, Warning } from "@mui/icons-material";
import { IconButton, Switch, Tooltip } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

interface TableProps {
  columns: string[];
  columnHeaders: string[];
  data: Array<Record<string, any>>;
  setSelectedOrder: React.Dispatch<
    React.SetStateAction<{ id: number; status: string } | null>
  >;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  statusFilter?: string;
  onViewOrder: (orderId: number) => void;
  onDownloadPdf?: (orderId: number) => void;
  isLoading?: boolean;
}

const SalesOrderTableComponent: React.FC<TableProps> = ({
  columns,
  columnHeaders,
  data: initialData,
  setSelectedOrder,
  currentPage,
  setCurrentPage,
  totalPages,
  statusFilter,
  onViewOrder,
  onDownloadPdf,
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const handleStatusChange = async (id: number, currentStatus: boolean) => {
    const newStatus = !currentStatus;

    // This function is no longer used as the tableData state is removed
  };

  const getRowNumber = (index: number) => {
    return currentPage * 10 + index + 1;
  };

  return (
    <div className="w-full">
      {/* Table Container */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px] bg-white">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="left-0 z-10 bg-gray-50 w-16 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                #
              </th>
              {columnHeaders.map((header, index) => (
                <th
                  key={index}
                  className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                    header === "Sales Order ID" ? "left-16 z-10 bg-gray-50" : ""
                  }`}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td
                  colSpan={columnHeaders.length + 1}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center">
                    <svg
                      className="animate-spin h-8 w-8 text-blue-500 mb-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <span className="text-lg font-medium text-gray-900 mb-1">
                      Loading...
                    </span>
                  </div>
                </td>
              </tr>
            ) : initialData.length > 0 ? (
              initialData.map((row, rowIndex) => {
                const rowNumber = getRowNumber(rowIndex);

                return (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 transition-colors">
                    <td className="left-0 z-10 bg-white px-4 py-4 text-sm text-gray-900">
                      {rowNumber}
                    </td>
                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={`px-4 py-4 text-sm text-gray-900 whitespace-nowrap ${
                          column === "id" ? "left-16 z-10 bg-white" : ""
                        }`}>
                        {column === "enabled" ? (
                          <div className="flex items-center">
                            <Switch
                              checked={row.enabled}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStatusChange(row.id, row.enabled);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              color="primary"
                              size="small"
                              inputProps={{ "aria-label": "status toggle" }}
                            />
                            <span
                              className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                row.enabled
                                  ? "bg-green-100 text-green-800 border border-green-300"
                                  : "bg-gray-100 text-gray-800 border border-gray-300"
                              }`}>
                              {row.enabled ? "ON" : "OFF"}
                            </span>
                          </div>
                        ) : column === "warning" ? (
                          row.warning ? (
                            <Tooltip
                              title="This order has items with low stock"
                              arrow>
                              <Warning
                                className="text-amber-500"
                                fontSize="small"
                              />
                            </Tooltip>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )
                        ) : column === "status" ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              row.status === "CLOSED"
                                ? "bg-gray-100 text-gray-800 border border-gray-300"
                                : row.status === "PENDING"
                                ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                                : row.status === "PROCESSING"
                                ? "bg-blue-100 text-blue-800 border border-blue-300"
                                : "bg-green-100 text-green-800 border border-green-300"
                            }`}>
                            {row.status || "Active"}
                          </span>
                        ) : column === "salesValue" ? (
                          <span className="font-medium">
                            ₹
                            {row.salesValue !== undefined
                              ? Number(row.salesValue).toFixed(2)
                              : "-"}
                          </span>
                        ) : column === "action" ? (
                          <Tooltip title="View Order Details" arrow>
                            <IconButton
                              onClick={() => onViewOrder(row.id)}
                              size="small"
                              color="primary">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : column === "download" ? (
                          <Tooltip title="Download PDF" arrow>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                onDownloadPdf && onDownloadPdf(row.id);
                              }}
                              size="small"
                              color="success"
                              sx={{ color: "#5EE65E" }}>
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : column === "address" ? (
                          <span>
                            {row.address
                              ? `${row.address.addressLineOne}, ${row.address.city}, ${row.address.country}`
                              : "N/A"}
                          </span>
                        ) : column === "creationDate" ? (
                          <span>{row.creationDate || "N/A"}</span>
                        ) : (
                          <span>
                            {row[column] !== undefined
                              ? row[column]?.toString()
                              : "N/A"}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={columnHeaders.length + 1}
                  className="px-4 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                    <p className="text-lg font-medium text-gray-900 mb-1">
                      No sales orders found
                    </p>
                    <p className="text-gray-500">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-2">
            {/* First Page Button */}
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              First
            </button>

            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
              disabled={currentPage === 0}
              className={`p-2 rounded-md border ${
                currentPage === 0
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Next Button */}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
              }
              disabled={currentPage === totalPages - 1}
              className={`p-2 rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Last Page Button */}
            <button
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
              className={`px-3 py-2 text-xs font-medium rounded-md border ${
                currentPage === totalPages - 1
                  ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
              }`}>
              Last
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrderTableComponent;
